import * as React from "react"
import { Calendar as CalendarIcon, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Popover, PopoverTrigger, PopoverContent } from "./popover"
import { Calendar } from "./calendar"

const BASE = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"

const toISO = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
  return z.toISOString().slice(0, 10)
}

const MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"]
const SEL = "h-8 rounded-md border border-input bg-white px-1 text-sm"

// App-wide date picker: any <Input type="date"> becomes Popover + month/year dropdowns + Calendar + clear.
const DateInput = React.forwardRef(({ className, value, defaultValue, onChange, onBlur, placeholder, disabled, min, max, dir, readOnly, ...props }, ref) => {
  const [open, setOpen] = React.useState(false)
  const [inner, setInner] = React.useState(defaultValue || "")
  const cur = value !== undefined ? value : inner
  const v = cur ? String(cur).slice(0, 10) : ""
  const selected = v ? new Date(v + "T00:00:00") : undefined
  const fromDate = min ? new Date(String(min).slice(0, 10) + "T00:00:00") : undefined
  const toDate = max ? new Date(String(max).slice(0, 10) + "T00:00:00") : undefined
  const [view, setView] = React.useState(selected || new Date())
  React.useEffect(() => { if (open) setView(selected || new Date()) }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const emit = (val) => {
    const ev = { target: { value: val, name: props.name }, currentTarget: { value: val, name: props.name } }
    if (value === undefined) setInner(val)
    onChange && onChange(ev)
    onBlur && onBlur(ev)
  }
  const now = new Date().getFullYear()
  const y0 = fromDate ? fromDate.getFullYear() : Math.min(1950, view.getFullYear())
  const y1 = toDate ? toDate.getFullYear() : Math.max(now + 15, view.getFullYear())
  const years = Array.from({ length: y1 - y0 + 1 }, (_, i) => y1 - i)
  const tid = props["data-testid"]
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && !readOnly && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          ref={ref}
          disabled={disabled}
          className={cn(BASE, "items-center justify-between gap-1 text-start", className)}
          {...props}
        >
          <span dir="ltr" className={cn("truncate", v ? "font-mono-nums" : "text-muted-foreground")}>
            {v || (placeholder || "اختر التاريخ")}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {v && !disabled && !readOnly && (
              <span role="button" tabIndex={-1} title="مسح التاريخ" data-testid={tid ? `${tid}-clear` : undefined}
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); emit("") }}
                className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <CalendarIcon className="h-4 w-4 opacity-50" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" dir="rtl" data-testid="date-popover">
        <div className="flex items-center gap-1 px-3 pt-3">
          <select value={view.getMonth()} onChange={(e) => setView(new Date(view.getFullYear(), Number(e.target.value), 1))} className={cn(SEL, "flex-1")} data-testid="date-month-select">
            {MONTHS.map((n, i) => <option key={n} value={i}>{n}</option>)}
          </select>
          <select value={view.getFullYear()} onChange={(e) => setView(new Date(Number(e.target.value), view.getMonth(), 1))} className={SEL} data-testid="date-year-select">
            {years.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <Calendar
          mode="single"
          selected={selected}
          month={view}
          onMonthChange={setView}
          fromDate={fromDate}
          toDate={toDate}
          initialFocus
          onSelect={(d) => { emit(d ? toISO(d) : ""); setOpen(false); }}
        />
        <div className="flex justify-between border-t px-3 py-2 text-xs">
          <button type="button" className="font-semibold text-blue-600 hover:underline" data-testid="date-today" onClick={() => { emit(toISO(new Date())); setOpen(false) }}>اليوم</button>
          <button type="button" className="font-semibold text-rose-600 hover:underline" data-testid="date-clear" onClick={() => { emit(""); setOpen(false) }}>مسح التاريخ</button>
        </div>
      </PopoverContent>
    </Popover>
  )
})
DateInput.displayName = "DateInput"

// <Input type="month">: year dropdown + 12 month buttons + clear. Value "YYYY-MM".
const MonthInput = React.forwardRef(({ className, value, onChange, placeholder, disabled, min, max, dir, readOnly, ...props }, ref) => {
  const [open, setOpen] = React.useState(false)
  const v = value ? String(value).slice(0, 7) : ""
  const [yr, setYr] = React.useState(v ? Number(v.slice(0, 4)) : new Date().getFullYear())
  React.useEffect(() => { if (open) setYr(v ? Number(v.slice(0, 4)) : new Date().getFullYear()) }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const emit = (val) => onChange && onChange({ target: { value: val, name: props.name }, currentTarget: { value: val, name: props.name } })
  const now = new Date().getFullYear()
  const years = Array.from({ length: Math.max(now + 5, yr) - Math.min(2015, yr) + 1 }, (_, i) => Math.max(now + 5, yr) - i)
  const label = v ? `${MONTHS[Number(v.slice(5, 7)) - 1] || ""} ${v.slice(0, 4)}` : (placeholder || "اختر الشهر")
  const tid = props["data-testid"]
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && !readOnly && setOpen(o)}>
      <PopoverTrigger asChild>
        <button type="button" ref={ref} disabled={disabled} className={cn(BASE, "items-center justify-between gap-1 text-start", className)} {...props}>
          <span className={cn("truncate", !v && "text-muted-foreground")}>{label}</span>
          <span className="flex shrink-0 items-center gap-1">
            {v && !disabled && !readOnly && (
              <span role="button" tabIndex={-1} title="مسح" data-testid={tid ? `${tid}-clear` : undefined}
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); emit("") }}
                className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><X className="h-3.5 w-3.5" /></span>
            )}
            <CalendarIcon className="h-4 w-4 opacity-50" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" dir="rtl" data-testid="month-popover">
        <select value={yr} onChange={(e) => setYr(Number(e.target.value))} className={cn(SEL, "mb-2 w-full")} data-testid="month-year-select">
          {years.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((n, i) => {
            const k = `${yr}-${String(i + 1).padStart(2, "0")}`
            const off = (min && k < String(min).slice(0, 7)) || (max && k > String(max).slice(0, 7))
            return <button type="button" key={k} disabled={off} data-testid={`month-opt-${i + 1}`} onClick={() => { emit(k); setOpen(false) }}
              className={cn("rounded-md py-1.5 text-xs transition-colors hover:bg-blue-50 disabled:opacity-30", k === v && "bg-blue-600 font-bold text-white hover:bg-blue-600")}>{n}</button>
          })}
        </div>
        <div className="mt-2 flex justify-between border-t pt-2 text-xs">
          <button type="button" className="font-semibold text-blue-600 hover:underline" onClick={() => { emit(toISO(new Date()).slice(0, 7)); setOpen(false) }}>الشهر الحالي</button>
          <button type="button" className="font-semibold text-rose-600 hover:underline" data-testid="month-clear" onClick={() => { emit(""); setOpen(false) }}>مسح</button>
        </div>
      </PopoverContent>
    </Popover>
  )
})
MonthInput.displayName = "MonthInput"

// <Input type="number">: keeps the typed text (so "0." / "1.05" aren't clobbered by Number() in parents) and accepts Arabic digits / ٫ / , as decimal.
const AR_DIGITS = { "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4", "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9", "۰": "0", "۱": "1", "۲": "2", "۳": "3", "۴": "4", "۵": "5", "۶": "6", "۷": "7", "۸": "8", "۹": "9" }
const normNum = (raw) => {
  let t = String(raw).replace(/[٠-٩۰-۹]/g, (d) => AR_DIGITS[d]).replace(/[٫,،]/g, ".").replace(/[^0-9.-]/g, "")
  t = t.replace(/(?!^)-/g, "")
  const i = t.indexOf(".")
  return i === -1 ? t : t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, "")
}
const NumberInput = React.forwardRef(({ className, value, onChange, step, min, max, ...props }, ref) => {
  const ext = value === null || value === undefined ? "" : String(value)
  const [draft, setDraft] = React.useState(ext)
  React.useEffect(() => {
    const same = draft === ext || (draft !== "" && ext !== "" && Number(draft) === Number(ext))
    if (!same) setDraft(ext)
  }, [ext]) // eslint-disable-line react-hooks/exhaustive-deps
  const handle = (e) => {
    const t = normNum(e.target.value)
    setDraft(t)
    if (onChange) onChange({ ...e, target: { ...e.target, value: t, name: props.name }, currentTarget: { value: t, name: props.name } })
  }
  return <input type="text" inputMode="decimal" dir={props.dir || "ltr"} className={cn(BASE, className)} ref={ref} value={draft} onChange={handle} {...props} />
})
NumberInput.displayName = "NumberInput"

const toPin = (v) => String(v).replace(/[٠-٩۰-۹]/g, (d) => AR_DIGITS[d]).replace(/\D/g, "").slice(0, 6)
// Search boxes: block browser autofill (it was typing the login phone into search fields)
const SearchInput = React.forwardRef(({ className, onFocus, ...props }, ref) => {
  const [locked, setLocked] = React.useState(true)
  return <input type="text" autoComplete="off" name={`q-${props["data-testid"] || "search"}`} data-lpignore="true" data-form-type="other"
    readOnly={locked} onFocus={(e) => { setLocked(false); onFocus && onFocus(e) }} className={cn(BASE, className)} ref={ref} {...props} />
})

const Input = React.forwardRef(({ className, type, pin, ...props }, ref) => {
  if (pin) {
    const { onChange, placeholder, ...rest } = props
    return <input type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="new-password" maxLength={6} dir="ltr" placeholder={placeholder || "6 أرقام"}
      className={cn(BASE, "tracking-[0.3em]", className)} ref={ref} {...rest}
      onChange={(e) => { const v = toPin(e.target.value); onChange && onChange({ ...e, target: { ...e.target, value: v, name: props.name }, currentTarget: { value: v, name: props.name } }) }} />
  }
  if (type === "number") return <NumberInput className={className} ref={ref} {...props} />
  if (type === "date") return <DateInput className={className} ref={ref} {...props} />
  if (type === "month") return <MonthInput className={className} ref={ref} {...props} />
  const isSearch = !type && /ابحث|بحث|search/i.test(`${props.placeholder || ""} ${props["data-testid"] || ""}`)
  if (isSearch) return <SearchInput className={className} ref={ref} {...props} />
  return (
    <input
      type={type}
      className={cn(BASE, className)}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
